import { Box, Button, HStack, Icon, Spacer, Text, VStack } from '@chakra-ui/react';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FaPlus } from 'react-icons/fa';
import { MdDelete, MdDragIndicator, MdEdit } from 'react-icons/md';

import { ActionsService, CommitmentGoalsService, IndicatorsService } from '@/client/index.ts';

import IndicatorAddDialog from '../indicators/index/IndicatorAddDialog.tsx';
import { CommitmentGoalAddDialog } from './CommitmentGoalAddDialog.tsx';
import { CommitmentGoalDeleteDialog } from './CommitmentGoalDeleteDialog.tsx';
import { IndicatorDeleteDialog } from './IndicatorDeleteDialog.tsx';

function getGoalsQueryOptions({ slug, commitment_id }: { slug: string; commitment_id: string }) {
  return {
    queryKey: ['commitments_goals', { slug, commitment_id }],
    queryFn: async () =>
      commitment_id
        ? await CommitmentGoalsService.readGoals({
            companySlug: slug,
            sort: 'order',
            filter: `commitment_id=${commitment_id}`,
          })
        : await Promise.resolve(null),
    enabled: !!commitment_id,
  };
}

function getActionsQueryOptions({ slug, commitment_id }: { slug: string; commitment_id: string }) {
  return {
    queryKey: ['commitments_actions', { slug, commitment_id }],
    queryFn: async () =>
      commitment_id
        ? await ActionsService.readActions({
            companySlug: slug,
            sort: 'order',
            filter: `commitment_id=${commitment_id}`,
          })
        : await Promise.resolve(null),
    enabled: !!commitment_id,
  };
}

function getIndicatorsQueryOptions({ slug, commitment_id }: { slug: string; commitment_id: string }) {
  return {
    queryKey: ['commitments_indicators', { slug, commitment_id }],
    queryFn: async () =>
      commitment_id
        ? await IndicatorsService.readIndicators({
            filter: `commitment_id=${commitment_id}`,
            sort: 'order',
            companySlug: slug,
          })
        : await Promise.resolve(null),
    enabled: !!commitment_id,
  };
}

export function CommitmentGoals({
  slug,
  commitment_id,
  editMode,
}: {
  slug: string;
  commitment_id: string;
  editMode: boolean;
}) {
  const { data: goals, isLoading } = useQuery(getGoalsQueryOptions({ slug, commitment_id }));

  return (
    <Droppable droppableId={commitment_id} type="goal" isDropDisabled={!editMode}>
      {(provided, snapshot) => (
        <>
          <VStack
            ref={provided.innerRef}
            {...provided.droppableProps}
            gap={2}
            width="full"
            bg={snapshot.isDraggingOver ? 'gray.100' : 'transparent'}
            borderRadius="md"
            mb={2}
          >
            {isLoading ? (
              <Text fontSize="xs" color="gray.500" mb={1}>
                Chargement des objectifs...
              </Text>
            ) : goals?.data.length === 0 ? (
              <Text fontSize="xs" color="gray.500" mb={1}>
                Aucun objectif trouvé
              </Text>
            ) : (
              goals?.data.map((goal, index) => (
                <Draggable draggableId={goal.id ?? ''} index={index} key={goal.id} isDragDisabled={!editMode}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      fontSize="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      shadow={snapshot.isDragging ? 'md' : 'sm'}
                      p={2}
                      borderRadius="md"
                      bg="white"
                      width="full"
                    >
                      <HStack>
                        {editMode && (
                          <Icon size={'sm'}>
                            <MdDragIndicator />
                          </Icon>
                        )}
                        {goal.description}
                        {editMode && (
                          <>
                            <Spacer />
                            <Icon size={'sm'} cursor={'pointer'}>
                              <MdEdit />
                            </Icon>
                            <CommitmentGoalDeleteDialog
                              commitmentId={commitment_id}
                              commitmentGoalId={goal.id ?? ''}
                              commitmentGoalName={goal.description ?? ''}
                            >
                              <Icon size={'sm'} color="red.500" cursor={'pointer'}>
                                <MdDelete />
                              </Icon>
                            </CommitmentGoalDeleteDialog>
                          </>
                        )}
                      </HStack>
                    </Box>
                  )}
                </Draggable>
              ))
            )}

            {provided.placeholder}
          </VStack>
          {editMode && (
            <CommitmentGoalAddDialog slug={slug} commitmentId={commitment_id} order={goals?.data.length ?? 0} />
          )}
        </>
      )}
    </Droppable>
  );
}

export function CommitmentActions({
  slug,
  commitment_id,
  editMode,
}: {
  slug: string;
  commitment_id: string;
  editMode: boolean;
}) {
  const { data: actions, isLoading } = useQuery(getActionsQueryOptions({ slug, commitment_id }));

  return (
    <Droppable droppableId={commitment_id} type="action" isDropDisabled={!editMode}>
      {(provided, snapshot) => (
        <VStack
          ref={provided.innerRef}
          {...provided.droppableProps}
          gap={2}
          width="full"
          bg={snapshot.isDraggingOver ? 'gray.100' : 'transparent'}
          borderRadius="md"
          mb={2}
        >
          {isLoading ? (
            <Text fontSize="xs" color="gray.500" mb={1}>
              Chargement des actions...
            </Text>
          ) : actions?.data.length === 0 ? (
            <Text fontSize="xs" color="gray.500" mb={1}>
              Aucune action trouvée
            </Text>
          ) : (
            actions?.data.map((action, index) => (
              <Draggable draggableId={action.id ?? ''} index={index} key={action.id} isDragDisabled={!editMode}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    fontSize="sm"
                    border="1px solid"
                    borderColor="gray.200"
                    shadow={snapshot.isDragging ? 'md' : 'sm'}
                    p={2}
                    borderRadius="md"
                    bg="white"
                    width="full"
                  >
                    <HStack>
                      {editMode && (
                        <Icon size={'sm'}>
                          <MdDragIndicator />
                        </Icon>
                      )}
                      {action.name}
                      {editMode && (
                        <>
                          <Spacer />
                          <Icon size={'sm'} opacity={0.5} cursor={'not-allowed'}>
                            <MdEdit />
                          </Icon>
                          <Icon size={'sm'} opacity={0.5} cursor={'not-allowed'}>
                            <MdDelete />
                          </Icon>
                        </>
                      )}
                    </HStack>
                  </Box>
                )}
              </Draggable>
            ))
          )}
          {provided.placeholder}
        </VStack>
      )}
    </Droppable>
  );
}

export function CommitmentIndicators({
  slug,
  commitment_id,
  editMode,
}: {
  slug: string;
  commitment_id: string;
  editMode: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: indicators, isLoading } = useQuery(getIndicatorsQueryOptions({ slug, commitment_id }));

  return (
    <Droppable droppableId={commitment_id} type="indicator" isDropDisabled={!editMode}>
      {(provided, snapshot) => (
        <>
          <VStack
            ref={provided.innerRef}
            {...provided.droppableProps}
            gap={2}
            width="full"
            bg={snapshot.isDraggingOver ? 'gray.100' : 'transparent'}
            borderRadius="md"
            mb={2}
          >
            {isLoading ? (
              <Text fontSize="xs" color="gray.500" mb={1}>
                Chargement des indicateurs...
              </Text>
            ) : indicators?.data.length === 0 ? (
              <Text fontSize="xs" color="gray.500" mb={1}>
                Aucun indicateur trouvé
              </Text>
            ) : (
              indicators?.data.map((indicator, index) => (
                <Draggable draggableId={indicator.id ?? ''} index={index} key={indicator.id} isDragDisabled={!editMode}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      fontSize="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      shadow={snapshot.isDragging ? 'md' : 'sm'}
                      p={2}
                      borderRadius="md"
                      bg="white"
                      width="full"
                    >
                      <HStack>
                        {editMode && (
                          <Icon size={'sm'}>
                            <MdDragIndicator />
                          </Icon>
                        )}
                        {indicator.name}
                        {editMode && (
                          <>
                            <Spacer />
                            <Icon size={'sm'} cursor={'pointer'}>
                              <MdEdit />
                            </Icon>
                            <IndicatorDeleteDialog
                              commitmentId={commitment_id}
                              indicatorId={indicator.id ?? ''}
                              indicatorName={indicator.name}
                            >
                              <Icon size={'sm'} color="red.500" cursor={'pointer'}>
                                <MdDelete />
                              </Icon>
                            </IndicatorDeleteDialog>
                          </>
                        )}
                      </HStack>
                    </Box>
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </VStack>
          {editMode && (
            <IndicatorAddDialog
              commitmentId={commitment_id}
              order={indicators?.data.length ?? 0}
              onSuccess={() => {
                queryClient.invalidateQueries({
                  queryKey: ['commitments_indicators', { slug, commitment_id }],
                });
              }}
            >
              <Button w="full" variant="outline" borderStyle={'dashed'} borderColor={'gray.300'}>
                Ajouter un indicateur <FaPlus size={16} />
              </Button>
            </IndicatorAddDialog>
          )}
        </>
      )}
    </Droppable>
  );
}
